import { useMemo, useState, type CSSProperties } from 'react'
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined, WarningOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime } from '@/shared/lib/format'
import { PageHeader } from '@/shared/ui/PageHeader'
import { ClosedHiddenTag } from '@/shared/ui/table/ClosedHidden'
import { useClosedHidden } from '@/shared/ui/table/useClosedHidden'
import { STATE_ALL, STATE_OPEN_ONLY } from '@/shared/ui/table/closedFilter'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions, type RowAction } from '@/shared/ui/table/RowActions'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { fetchVenues } from '@/features/venue/venueApi'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import { TicketFilterBar } from '@/features/crm/CrmFilterBars'
import {
  countClosedTickets,
  createTicket,
  fetchTicketPage,
  ticketAcceptanceStep,
  ticketEditPlan,
  ticketStage,
  ticketStartBlocked,
  ticketTransition,
  TICKET_CATEGORIES,
  TICKET_NEXT,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
  type Ticket,
  type TicketAcceptanceStep,
  type TicketCategory,
  type TicketPriority,
  type TicketStage,
  type TicketStatus,
} from '@/features/crm/crmApi'
import { TicketCloseModal } from '@/features/crm/TicketCloseModal'
import { TicketEditModal } from '@/features/crm/TicketEditModal'
import { CrmAssignModal } from '@/features/crm/CrmAssignModal'
import { TicketDetailsDrawer } from '@/features/crm/TicketDetailsDrawer'
import { actionReason } from '@/features/crm/actionReason'
import { SubscriberSelect } from '@/features/subscriber/SubscriberSelect'
import { submitOnValid } from '@/shared/lib/submitOnValid'

const STATUS_COLOR: Record<string, string> = {
  OPEN: 'blue',
  IN_PROGRESS: 'gold',
  RESOLVED: 'green',
  REOPENED: 'orange',
  CLOSED: 'default',
}

const ACCEPTANCE_COLOR: Partial<Record<TicketStage, string>> = {
  UNASSIGNED: 'default',
  ASSIGNED: 'purple',
  RECEIVED: 'cyan',
  CONFIRMED: 'green',
}

const PRIORITY_COLOR: Record<string, string> = {
  LOW: 'default',
  NORMAL: 'blue',
  HIGH: 'orange',
  URGENT: 'red',
}

/**
 * TANASIZ o'tishlar: holat nomi → endpoint nomi.
 *
 * ⚠️ `CLOSED` bu yerda ATAYLAB YO'Q — yopish SABAB talab qiladi
 * (`ticket_closed_consistent` cheklovi), ya'ni u tugmani darhol
 * yubormaydi, MODAL ochadi.
 *
 * ⚠️ Ilgari bu xarita `TRANSITION_PATH[to] ?? 'close'` bilan
 * ishlatilardi va aynan o'sha zaxira qiymat ikki nuqson bergan:
 *
 *   · «Yopish» tugmasi tanasiz `/close` ni chaqirardi va HAR bosishda
 *     400 qaytardi — murojaatni umuman yopib bo'lmasdi (tester A5);
 *   · xaritada bo'lmagan HAR QANDAY holat jimgina `close` ga
 *     aylanardi — ya'ni noma'lum tugma eng og'ir, qaytarib bo'lmaydigan
 *     amalni bajarardi.
 *
 * Bu `LeadsPage` dagi `?? 'lost'` bilan AYNAN bir xil oiladan. Endi tip
 * `Record<TicketStatus, ...>` emas, aniq uch kalitli obyekt va zaxira
 * qiymat umuman yo'q: xaritada bo'lmagan holat uchun kod
 * KOMPILYATSIYA bo'lmaydi, jimgina boshqa amalga aylanmaydi.
 */
const PLAIN_TRANSITION = {
  IN_PROGRESS: 'start',
  RESOLVED: 'resolve',
  REOPENED: 'reopen',
} as const

type PlainTransitionTarget = keyof typeof PLAIN_TRANSITION

interface TicketFormValues {
  subjectKind: 'VENUE' | 'SUBSCRIBER'
  venueId?: string
  subscriberId?: string
  category: TicketCategory
  priority: TicketPriority
  subject: string
  description?: string
}

/**
 * Kechikkanmi — {@code dueAt} o'tgan va tiket hali hal qilinmagan (serverdagi `onlyOverdue` bilan bir xil).
 *
 * ⚠️ Hal qilingan va yopilgan tiket kechikkan deb belgilanmaydi: u allaqachon tugagan
 * va uni qizil qilib ko'rsatish ro'yxatni "hammasi yomon" holatga
 * keltirib, haqiqiy kechikkanlarni ko'rinmas qilardi.
 *
 * ⚠️ Bu FAQAT ekrandagi belgi. «Faqat kechikkanlar» filtri SERVERDA
 * hisoblanadi (`onlyOverdue`) — mijozdagi hisob joriy sahifadan
 * boshqasini ko'rmaydi va "kechikkan yo'q" degan yolg'on beradi.
 */
const isOverdue = (row: Ticket) =>
  row.dueAt !== null
  && row.dueAt !== undefined
  && row.status !== 'RESOLVED'
  && row.status !== 'CLOSED'
  && new Date(row.dueAt).getTime() < Date.now()

/**
 * Murojaatlar (tiketlar) — mijoz shikoyati va uning taqdiri.
 *
 * <h2>⚠️ SLA KO'RINADI, YASHIRINMAYDI</h2>
 * Backend uch narsani beradi: {@code dueAt} (muddat), {@code firstResponseAt}
 * (birinchi javob QACHON berilgani) va {@code onlyOverdue} filtri. Ularsiz
 * jadval "ochiq tiketlar ro'yxati" bo'lib qolardi va eng muhim savol —
 * "qaysi biri kechikdi" — javobsiz qolardi.
 *
 * <h2>⚠️ FAQAT RUXSAT ETILGAN O'TISH KO'RSATILADI</h2>
 * Holat mashinasi {@code TICKET_NEXT} da. Hamma tugmani ko'rsatish
 * backend rad javobi bilan tugardi va operator sababini bilmasdi —
 * bu xato panelning boshqa joylarida allaqachon ushlangan.
 *
 * <h2>⚠️ HAR O'TISH O'Z ENDPOINTI BILAN</h2>
 * "status" maydonini yangilash bilan almashtirilmagan: backend har
 * o'tishda boshqa ish bajaradi ({@code resolve} → {@code resolvedAt},
 * {@code close} → {@code closedAt}, {@code reopen} → ikkalasini
 * tozalaydi). Umumiy yangilash ularni chetlab o'tardi.
 *
 * <h2>⚠️ YOPILGAN MUROJAATLAR SUKUT BO'YICHA KO'RINMAYDI (ADR-033)</h2>
 * Panel `includeClosed` ni HAR so'rovda ochiq yuboradi
 * ({@code ticketListParams}) — serverdagi sukut qiymat `true`, ya'ni
 * yuborilmagani «hammasi» degani.
 *
 * <p>⚠️ «Yopiq» bu yerda FAQAT {@code CLOSED}. {@code RESOLVED} QOLADI:
 * u {@code REOPENED} ga o'tishi mumkin ({@code TICKET_NEXT}), ya'ni ish
 * hali tugamagan bo'lishi mumkin.
 *
 * <p>⚠️ Yagona yo'l — HOLAT FILTRI. Filtr {@code CLOSED} ni
 * tanlaganda server bayroqni e'tiborsiz qoldiradi va yopilganlarni
 * ROSTDAN qaytaradi ({@code TicketQuery.excludesClosed}).
 */
export function TicketsPage() {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const queryClient = useQueryClient()
  /*
    ⚠️ IKKI HUQUQ BIRGA, `canAny` EMAS. Ilgari «Yopish» tugmasi ikki
    qavat `<Can>` ichida turardi (tashqarisi `crm.ticket.update`, ichi
    `crm.ticket.close`) — ya'ni VA. `RowActions.permission` esa YOKI
    semantikasida, shuning uchun yopish sharti bu yerda `canAll` bilan
    hisoblanadi va amalga `hidden` orqali beriladi.
  */
  const { can, canAll } = usePermissions()
  const { user } = useAuth()
  const actors = useActorDirectory()
  const canClose = canAll('crm.ticket.update', 'crm.ticket.close')
  /*
    ⚠️ Mas'ul tanlovi xodimlar ro'yxatini `identity` dan oladi. `user.read`
    yo'q odamda so'rov 403 berardi va tanlov BO'SH ochilardi — shuning
    uchun band O'CHIQ bo'ladi va sababi yorlig'ida yoziladi.
  */
  const canPickAssignee = can('user.read')
  const [createOpen, setCreateOpen] = useState(false)
  /** «Yopish» — modal orqali, sababi `TicketCloseModal` javadoc'ida */
  const [closing, setClosing] = useState<Ticket | null>(null)
  /**
   * Tahrirlash va MAS'UL — ikki alohida oyna.
   *
   * ⚠️ Birlashtirilmadi: `TicketServiceImpl.update` YOPILGAN tiketni rad
   * etadi (`NotEditable`), `assign` esa holatni umuman tekshirmaydi.
   * Ikkalasini bitta formaga yig'ish yopilgan tiketning egasini
   * almashtirish imkonini YO'QOTARDI — batafsil `CrmAssignModal` da.
   */
  const [editing, setEditing] = useState<Ticket | null>(null)
  const [assigning, setAssigning] = useState<Ticket | null>(null)
  const [viewingId, setViewingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  /*
    Ob'ektlar ro'yxati — mavzu tanlash uchun.

    ⚠️ Faqat modal OCHILGANDA so'raladi: ro'yxat sahifaning asosiy
    vazifasiga kirmaydi va uni har ochilishda tortish keraksiz
    so'rov bo'lardi.
  */
  const venues = useQuery({
    queryKey: queryKeys.venues.list(null),
    queryFn: () => fetchVenues(null),
    enabled: createOpen,
  })
  const [form] = Form.useForm<TicketFormValues>()

  /*
    Bo'sh holat IKKI XIL bo'lishi uchun jadval holati sahifaga ham kerak.
    Manba bitta — URL, ya'ni `DataTable` va bu yer bir xil holatni
    ko'radi va ular ajralib keta olmaydi.
  */
  const { state: tableState, patch: patchTable, reset: resetTable } = useTableUrlState()
  const isFiltered = tableState.search !== '' || Object.keys(tableState.filters).length > 0

  /*
    ⚠️ SUKUT KO'RINISH YOPILGAN MUROJAATLARSIZ (ADR-033). `RESOLVED`
    esa QOLADI — u qayta ochilishi mumkin bo'lgan holat (`TICKET_NEXT`),
    ya'ni tugagan ish emas.
  */
  const closedFilterValue = tableState.filters['status']?.[0] ?? STATE_OPEN_ONLY
  const hiddenCounts = useClosedHidden<Ticket>({
    queryKey: queryKeys.crm.ticketList,
    state: tableState,
    fetchPage: fetchTicketPage,
    countClosed: countClosedTickets,
    enabled: closedFilterValue === STATE_OPEN_ONLY,
  })
  const showAllStates = () =>
    patchTable({ page: 1, filters: { ...tableState.filters, status: [STATE_ALL] } })

  /**
   * Holat ro'yxati USTUN filtri uchun ham kerak.
   *
   * ⚠️ Usiz filtr sahifa almashtirilishida JIMGINA tushib qolardi:
   * `DataTable.handleChange` filtrlarni antd bergan ro'yxatdan qayta
   * quradi, antd esa faqat `filters` e'lon qilgan ustunlarni biladi.
   */
  const statusOptions = useMemo(
    () => [
      { value: STATE_OPEN_ONLY, label: t('dataTable.stateOpenOnly') },
      ...TICKET_STATUSES.map((s) => ({ value: s, label: t(`crm.ticketStatus.${s}`) })),
      { value: STATE_ALL, label: t('dataTable.stateAll') },
    ],
    [t],
  )

  const refresh = () => void queryClient.invalidateQueries({ queryKey: queryKeys.crm.ticketList })

  const transition = useMutation({
    mutationFn: ({ id, to }: { id: string; to: PlainTransitionTarget }) =>
      ticketTransition(id, PLAIN_TRANSITION[to]),
    onSuccess: refresh,
    onError: (err) => setError(translateError(err, t)),
  })

  const accept = useMutation({
    mutationFn: ({ id, step }: { id: string; step: TicketAcceptanceStep }) => ticketTransition(id, step),
    onSuccess: refresh,
    onError: (err) => setError(translateError(err, t)),
  })

  const create = useMutation({
    mutationFn: ({ subjectKind: _subjectKind, ...draft }: TicketFormValues) => createTicket(draft),
    onSuccess: () => {
      setCreateOpen(false)
      form.resetFields()
      refresh()
    },
    // ⚠️ `onError` ATAYLAB yo'q: bu mutatsiya OYNAGA bog'langan
    // (`confirmLoading={create.isPending}`) va xatosi sahifa banneriga
    // chiqsa, modal niqobi ortida qolardi. `transition` esa oynasiz —
    // uning xatosi bannerda to'g'ri joyda.
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin */
  const closeCreate = () => {
    create.reset()
    setCreateOpen(false)
  }

  // ⚠️ Tartib raqami ustuni YO'Q — `DataTable` uni o'zi qo'shadi va
  // sahifa bo'yicha uzluksiz sanaydi (2-sahifada 26, 27, …).
  const columns = useMemo<ColumnsType<Ticket>>(() => [
    {
      key: 'subject',
      title: t('crm.ticket.subject'),
      width: 260,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <Typography.Text strong>{row.subject}</Typography.Text>
            {isOverdue(row) ? (
              <Tooltip title={t('crm.ticket.overdueHint')}>
                <Tag color="red" icon={<WarningOutlined />}>
                  {t('crm.ticket.overdue')}
                </Tag>
              </Tooltip>
            ) : null}
          </Space>
          {row.subscriberLogin ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.subscriberLogin}
            </Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      // ⚠️ Ustun kaliti FILTR kaliti bilan bir xil va `filters` MAJBURIY —
      // sabab `statusOptions` izohida.
      key: 'status',
      title: t('subscribers.columns.status'),
      width: 190,
      filters: statusOptions.map((o) => ({ text: o.label, value: o.value })),
      filterMultiple: false,
      render: (_, row) => {
        const stage = ticketStage(row)
        const acceptanceColor = stage === null ? undefined : ACCEPTANCE_COLOR[stage]
        return (
          <Space size={4} wrap>
            <Tag color={STATUS_COLOR[row.status]}>{t(`crm.ticketStatus.${row.status}`)}</Tag>
            {acceptanceColor ? <Tag color={acceptanceColor}>{t(`crm.ticketStage.${stage}`)}</Tag> : null}
            {row.acknowledgeOverdue ? (
              <Tooltip title={t('crm.ticket.ackOverdueHint')}>
                <Tag color="red" icon={<WarningOutlined />}>
                  {t('crm.ticket.ackOverdue')}
                </Tag>
              </Tooltip>
            ) : null}
          </Space>
        )
      },
    },
    {
      key: 'assignee',
      title: t('crm.ticket.assignee'),
      width: 200,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          {row.assignedTo ? (
            <Typography.Text>{actorLabel(row.assignedTo, actors, '—')}</Typography.Text>
          ) : (
            <Typography.Text type="warning">{t('crm.ticket.unassigned')}</Typography.Text>
          )}
          {row.teamName ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.teamName}
            </Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      key: 'assignedAt',
      title: t('crm.ticket.assignedAt'),
      width: 170,
      render: (_, row) => formatDateTime(row.assignedAt),
    },
    {
      key: 'dueAt',
      title: t('crm.ticket.dueAt'),
      width: 170,
      render: (_, row) => formatDateTime(row.dueAt),
    },
    {
      key: 'priority',
      title: t('crm.ticket.priority'),
      width: 110,
      render: (_, row) => (
        <Tag color={PRIORITY_COLOR[row.priority]}>{t(`crm.ticketPriority.${row.priority}`)}</Tag>
      ),
    },
    {
      key: 'category',
      title: t('crm.ticket.category'),
      width: 150,
      render: (_, row) => t(`crm.ticketCategory.${row.category}`),
    },
    {
      key: 'firstResponse',
      title: t('crm.ticket.firstResponse'),
      width: 170,
      /*
        ⚠️ "—" EMAS, ANIQ MATN. Birinchi javob berilmagani — SLA ning
        eng muhim ko'rsatkichi va uni bo'sh katak qilib qoldirish
        "ma'lumot yo'q" bo'lib o'qilardi. Aslida bu "hali javob
        berilmagan" degani.
      */
      render: (_, row) =>
        row.firstResponseAt ? (
          formatDateTime(row.firstResponseAt)
        ) : (
          <Typography.Text type="warning">{t('crm.ticket.noFirstResponse')}</Typography.Text>
        ),
    },
    {
      key: 'openedAt',
      title: t('crm.ticket.openedAt'),
      width: 170,
      render: (_, row) => formatDateTime(row.openedAt),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      /*
        ⚠️ `fixed: 'right'` — ustun jadval bilan birga SURILMAYDI.

        Jadvalda gorizontal scroll bor (`scroll.x`), ya'ni amallar
        ustuni o'ng chekkada qolib ekrandan chiqib ketardi: uchta
        holat tugmasidan faqat BITTASI ko'rinardi va operator
        qolganini umuman topa olmasdi.

        ⚠️ Bu brauzerda KO'Z BILAN ushlandi — o'lchov asboblarim buni
        KO'RMAYDI: katak siqilmagan, matn ham bo'linmagan, u shunchaki
        ko'rish maydonidan tashqarida qolgan. Ya'ni "muammo topilmadi"
        degan natija bu holatni qamramaydi.
      */
      fixed: 'right',
      render: (_, row) => {
        const next = TICKET_NEXT[row.status as TicketStatus] ?? []
        /*
          ⚠️ «O'tish yo'q — menyu ham yo'q» qoidasi OLIB TASHLANDI.

          Ilgari bu yerda `if (next.length === 0) return null` turardi,
          ya'ni YOPILGAN tiketda menyu umuman chizilmasdi. Endi u yerda
          ikki band bor va ikkalasi ham kerak:
            · «O'zgartirish» — O'CHIQ, sababi yorlig'ida (server
              `NotEditable` bilan rad etadi);
            · «Mas'ul» — ISHLAYDI: `TicketServiceImpl.assign` holatni
              tekshirmaydi va yopilgan tiketning egasini almashtirish
              hisobot uchun kerak bo'ladi.
        */
        const editBlock = ticketEditPlan(row).block
        const assignBlock = canPickAssignee ? null : 'noDirectory'
        const acceptanceStep = ticketAcceptanceStep(row, user?.id)
        const startBlocked = ticketStartBlocked(row)
        return (
          <RowActions
            items={[
              {
                key: 'details',
                label: t('crm.ticketAction.details'),
                onClick: () => setViewingId(row.id),
              },
              {
                key: 'accept',
                label: acceptanceStep ? t(`crm.ticketAction.${acceptanceStep}`) : '',
                onClick: () => {
                  if (acceptanceStep) accept.mutate({ id: row.id, step: acceptanceStep })
                },
                loading: accept.isPending,
                permission: 'crm.ticket.update',
                hidden: acceptanceStep === null,
              },
              {
                key: 'edit',
                label: actionReason(
                  t('common.edit'),
                  editBlock === null ? null : t(`crm.ticketEdit.blocked.${editBlock}`),
                ),
                onClick: () => setEditing(row),
                disabled: editBlock !== null,
                permission: 'crm.ticket.update',
              },
              {
                key: 'assign',
                label: actionReason(
                  t('crm.assign.action'),
                  assignBlock === null ? null : t(`crm.assign.blocked.${assignBlock}`),
                ),
                onClick: () => setAssigning(row),
                disabled: assignBlock !== null,
                permission: 'crm.ticket.update',
              },
              // ⚠️ Qaytish tipi ANIQ yozilgan: `map` ichida kontekst yo'q
              // va `permission: 'crm.ticket.update'` xom `string` ga
              // kengayib ketardi — huquq kodi tip darajasida tekshirilmay
              // qolardi (katalogda yo'q kod jimgina o'tib ketardi).
              ...next.map((to, index): RowAction => {
              /*
                ⚠️ IKKI xil amal, va ular BIR XIL bandga o'xshaydi.
                Aynan shu o'xshashlik A5 nuqsonini yashirgan edi:
                «Yopish» ham oddiy o'tish deb hisoblanib, tanasiz
                yuborilardi.

                · CLOSED  — SABAB talab qiladi, shuning uchun modal;
                · qolgani — tanasiz o'tish, darhol yuboriladi.
              */
              if (to === 'CLOSED') {
                /*
                  ⚠️ Yopish uchun ALOHIDA huquq — `crm.ticket.close`
                  (`TicketResource#close` javadoc'i: "qaytarib
                  bo'lmaydigan amal ko'proq ishonchni talab qiladi").
                  `canClose` = `crm.ticket.update` VA `crm.ticket.close`,
                  ya'ni usiz amal yopa OLMAYDIGAN odamga ko'rinmaydi.
                */
                return {
                  key: to,
                  label: t('crm.ticketAction.CLOSED'),
                  onClick: () => setClosing(row),
                  danger: true,
                  divider: true,
                  hidden: !canClose,
                }
              }
              // ⚠️ Zaxira qiymat O'RNIGA aniq tekshiruv: noma'lum
              // holat uchun amal UMUMAN chizilmaydi.
              const known = to === 'IN_PROGRESS' || to === 'RESOLVED' || to === 'REOPENED'
              const blocked = to === 'IN_PROGRESS' && startBlocked
              return {
                key: to,
                label: known
                  ? actionReason(
                      t(`crm.ticketAction.${to}`),
                      blocked ? t('crm.ticketAction.startBlocked') : null,
                    )
                  : '',
                onClick: () => {
                  if (known) transition.mutate({ id: row.id, to })
                },
                loading: transition.isPending,
                disabled: blocked,
                permission: 'crm.ticket.update',
                // ⚠️ Ajratuvchi FAQAT birinchi o'tishda — tahrir/mas'ul
                // guruhini holat guruhidan ajratadi. `CLOSED` hech
                // qachon birinchi emas (`TICKET_NEXT`), ya'ni ikki
                // chiziq yonma-yon tushmaydi.
                divider: index === 0,
                hidden: !known,
              }
              }),
            ]}
          />
        )
      },
    },
  ], [t, canClose, canPickAssignee, transition, accept, actors, user?.id, statusOptions])

  return (
    <>
      <PageHeader
        title={t('crm.ticket.title')}
        description={t('crm.ticket.subtitle')}
        extra={
          <Can permission="crm.ticket.create">
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              {t('crm.ticket.create')}
            </Button>
          </Can>
        }
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {/*
        ⚠️ SAHIFALASH, QIDIRUV VA FILTR — SERVERDA (`fetchTicketPage`).
        `TicketQuery` `page`/`size`/`search`/`status`/`category`/
        `priority`/`onlyOverdue` ni haqiqatdan qabul qiladi.

        ⚠️ Ro'yxat xatosi uchun alohida banner YO'Q — `DataTable` uni
        sababi va «qayta urinish» tugmasi bilan o'zi chizadi.
      */}
      <DataTable<Ticket>
        queryKey={queryKeys.crm.ticketList}
        fetchPage={fetchTicketPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="crm-tickets"
        stickyFirstColumn
        searchable
        searchPlaceholder={t('crm.ticket.searchPlaceholder')}
        toolbarExtra={
          <Space wrap size="small">
            <TicketFilterBar />
            <ClosedHiddenTag counts={hiddenCounts} onShowAll={showAllStates} />
          </Space>
        }
        scrollX={1702}
        onRow={(row) => isOverdue(row)
          ? { className: 'xsib-row-overdue', style: { '--xsib-row-overdue-bg': token.colorErrorBg } as CSSProperties }
          : {}}
        {...(isFiltered
          ? {
              emptyTitle: t('dataTable.emptyFiltered'),
              emptyDescription: t('dataTable.emptyFilteredHint'),
              emptyAction: { label: t('dataTable.resetFilters'), onClick: resetTable },
            }
          : {
              emptyTitle: t('crm.ticket.emptyTitle'),
              /*
                ⚠️ Bo'sh ro'yxat «murojaat yo'q» degani EMAS: yopilganlar
                sukut bo'yicha yashirin va ikkinchi yo'l ko'rsatilmasa
                operator ularni o'chgan deb o'qirdi.
              */
              ...(hiddenCounts && hiddenCounts.hidden > 0
                ? {
                    emptyDescription: t('dataTable.closedHiddenHint'),
                    emptyAction: { label: t('dataTable.stateAll'), onClick: showAllStates },
                  }
                : {
                    emptyDescription: t('crm.ticket.emptyHint'),
                    ...(can('crm.ticket.create')
                      ? {
                          emptyAction: {
                            label: t('crm.ticket.create'),
                            onClick: () => setCreateOpen(true),
                          },
                        }
                      : {}),
                  }),
            })}
      />

      {/* --- Tavsifni to'g'rilash: `PUT` TO'LIQ ALMASHTIRADI --- */}
      <TicketEditModal
        ticket={editing}
        onCancel={() => setEditing(null)}
        onDone={() => {
          setEditing(null)
          refresh()
        }}
      />

      {/* --- Mas'ul: ALOHIDA amal, sabablari `CrmAssignModal` da --- */}
      <CrmAssignModal
        kind="ticket"
        target={
          assigning === null
            ? null
            : {
                id: assigning.id,
                title: assigning.subject,
                assignedTo: assigning.assignedTo,
                team:
                  assigning.teamId && assigning.teamName
                    ? { id: assigning.teamId, name: assigning.teamName }
                    : null,
                progressAtRisk: Boolean(assigning.receivedAt) || assigning.status === 'IN_PROGRESS',
              }
        }
        onCancel={() => setAssigning(null)}
        onDone={() => {
          setAssigning(null)
          refresh()
        }}
      />

      <TicketDetailsDrawer
        ticketId={viewingId}
        onClose={() => setViewingId(null)}
        onChanged={refresh}
      />

      {/* --- Yopish: sabab MAJBURIY (baza cheklovi usiz qatorni yozmaydi) --- */}
      <TicketCloseModal
        ticket={closing}
        onCancel={() => setClosing(null)}
        onDone={() => {
          setClosing(null)
          refresh()
        }}
      />

      <Modal
        open={createOpen}
        title={t('crm.ticket.create')}
        onCancel={closeCreate}
        onOk={() => submitOnValid(form, (values) => create.mutate(values))}
        confirmLoading={create.isPending}
        okText={t('operation.create')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {create.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(create.error, t)}
          />
        ) : null}

        <Form form={form} layout="vertical" preserve={false}>
          {/*
            ⚠️ MAVZU (KIM HAQIDA) — MAJBURIY VA AYNAN BITTA.

            Backend qoidasi: "Tiketda aynan bitta mavzu bo'lishi kerak:
            abonent YOKI ob'ekt". Bu maydonlar formada YO'Q edi va
            operator "Yaratish" bosganda xom 400 xatosini olardi —
            nima qilish kerakligi haqida hech qanday ishorasiz.

            Shuning uchun tanlov formaning BOSHIDA: mavzuni tanlamasdan
            tiket yozishning ma'nosi yo'q.
          */}
          <Form.Item
            name="subjectKind"
            label={t('crm.ticket.about')}
            initialValue={user?.tenant.businessKind === 'VENUE' ? 'VENUE' : 'SUBSCRIBER'}
            extra={t('crm.ticket.aboutHint')}
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { value: 'VENUE', label: t('crm.ticket.aboutVenue') },
                { value: 'SUBSCRIBER', label: t('crm.ticket.aboutSubscriber') },
              ]}
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev: { subjectKind?: string }, next: { subjectKind?: string }) =>
              prev.subjectKind !== next.subjectKind
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('subjectKind') === 'VENUE' ? (
                <Form.Item
                  name="venueId"
                  label={t('crm.ticket.aboutVenue')}
                  rules={[{ required: true, message: t('crm.ticket.aboutRequired') }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    loading={venues.isPending}
                    options={(venues.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  name="subscriberId"
                  label={t('crm.ticket.aboutSubscriber')}
                  rules={[{ required: true, message: t('crm.ticket.aboutRequired') }]}
                >
                  <SubscriberSelect />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item
            name="subject"
            label={t('crm.ticket.subject')}
            rules={[{ required: true, message: t('crm.ticket.subjectRequired') }]}
          >
            <Input autoFocus />
          </Form.Item>
          <Form.Item
            name="category"
            label={t('crm.ticket.category')}
            initialValue="OTHER"
            rules={[{ required: true }]}
          >
            <Select
              options={TICKET_CATEGORIES.map((c) => ({
                value: c,
                label: t(`crm.ticketCategory.${c}`),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="priority"
            label={t('crm.ticket.priority')}
            initialValue="NORMAL"
            rules={[{ required: true }]}
          >
            <Select
              options={TICKET_PRIORITIES.map((p) => ({
                value: p,
                label: t(`crm.ticketPriority.${p}`),
              }))}
            />
          </Form.Item>
          <Form.Item name="description" label={t('crm.ticket.description')}>
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
